import React from 'react';
import { PolicyPage, Section, Bullets, MERCHANT } from './legal';

const DrivelinePrivacy = () => (
  <PolicyPage
    testid="driveline-privacy"
    title="Privacy Policy"
    intro="What Driveline collects, why, who it is shared with, and the choices you have. This covers
           both the workshops who subscribe to Driveline and the vehicle owners whose details a
           workshop records in it."
  >
    <Section title="Who controls your data" testid="privacy-controller">
      <p>
        {MERCHANT.legal}, {MERCHANT.address}, operates Driveline and is the data controller for
        workshop account information. For the customer and vehicle records a workshop enters, the
        workshop is the controller and we act as its processor — we hold and process that information
        on the workshop's instructions and do not use it for our own purposes.
      </p>
      <p>
        Questions or requests:{' '}
        <a className="text-teal-700 underline" href={`mailto:${MERCHANT.email}`}>{MERCHANT.email}</a>.
      </p>
    </Section>

    <Section title="What we collect" testid="privacy-collect">
      <p><b>From the workshop that subscribes</b></p>
      <Bullets items={[
        'Account details: workshop name, owner name, work email, mobile number, and a password stored only as a cryptographic hash.',
        'Business details you add for paperwork: address, GSTIN, PAN where entered, bank details printed on invoices, logo and brand colour.',
        'Staff details you add: names, designations, contact numbers, attendance, payroll and performance records.',
        'Billing records: plan, subscription status, payment dates, amounts, invoice numbers and the mandate reference from the payment gateway.',
        'Technical logs: IP address, browser type, timestamps and the actions taken in the account (used for security and the activity log).',
      ]} />
      <p className="pt-2"><b>About the workshop's own customers</b></p>
      <Bullets items={[
        'Name, prefix, phone number, email and address as entered by the workshop.',
        'Vehicle details: make, model, registration number, VIN or chassis number, odometer reading.',
        'Job records: estimates, invoices, payments, inspection findings and photographs, warranty certificates and service history.',
      ]} />
      <p>
        We never collect card numbers, UPI PINs or net-banking credentials. Payments are entered
        directly on Cashfree's secure pages and those credentials never reach our servers.
      </p>
    </Section>

    <Section title="Why we use it" testid="privacy-purpose">
      <Bullets items={[
        'To provide the service — create your documents, run your reports, and keep your account working.',
        'To take payment for your subscription and issue you a GST invoice.',
        'To send service communications: trial and renewal reminders, payment receipts and failures, security notices.',
        'To deliver documents you ask us to send on your behalf — an invoice email or warranty certificate to your customer.',
        'To keep the platform secure, investigate abuse, and maintain the audit trail your own compliance may require.',
        'To meet legal obligations, principally tax and accounting record-keeping.',
      ]} />
      <p>
        We do not sell personal data, we do not rent mailing lists, and we do not market to your
        customers.
      </p>
    </Section>

    <Section title="Who we share it with" testid="privacy-sharing">
      <p>Only with service providers needed to run Driveline, and only to the extent each one needs:</p>
      <Bullets items={[
        'Cashfree Payments — subscription collection, UPI AutoPay and eNACH mandates, refunds. Receives your name, email, phone and the amount.',
        'Cloud hosting and database providers — store the application and its data.',
        'Cloud file storage — stores inspection photographs, logos and bill attachments.',
        'Email delivery provider — sends the invoices, certificates and notices you trigger.',
        'AI providers (Google Gemini, OpenAI) — only for the optional features that need them, such as reading a scanned supplier bill or drafting a training plan. Content sent for these features is not used by us to train models.',
      ]} />
      <p>
        We also disclose data where the law requires it — a valid order from a court or authority — or
        to establish or defend a legal claim. If our business is ever transferred, we will tell you
        before your data moves, and the new owner is bound by this policy.
      </p>
    </Section>

    <Section title="Where it is stored" testid="privacy-location">
      <p>
        Data is stored on managed cloud infrastructure. Some providers listed above may process data
        outside India; where that happens we rely on their contractual data-protection commitments and
        share only what the feature requires.
      </p>
    </Section>

    <Section title="How long we keep it" testid="privacy-retention">
      <Bullets items={[
        'Active accounts: for as long as the account exists.',
        'Cancelled accounts: retained in read-only form so you can export, then deleted within 30 days of a written deletion request.',
        'Financial records (our GST invoices to you): retained for 8 years as Indian tax law requires, even after deletion of the rest.',
        'Security and activity logs: up to 12 months.',
      ]} />
    </Section>

    <Section title="How we protect it" testid="privacy-security">
      <Bullets items={[
        'Encrypted connections (HTTPS/TLS) for all traffic.',
        'Passwords stored as salted hashes — never in readable form.',
        'Each workshop\'s records are scoped to its own account; one workshop cannot read another\'s data.',
        'Role-based permissions so you control which staff member sees which module.',
        'Brute-force protection on login, session cookies with CSRF protection, and an activity log of sensitive actions.',
        'Regular backups of the database.',
      ]} />
      <p>
        No system is perfectly secure. If a breach affects your data, we will notify you and the
        relevant authority without undue delay, with what we know and what we are doing about it.
      </p>
    </Section>

    <Section title="Laws we follow" testid="privacy-laws">
      <p>
        We handle personal data in line with India’s Digital Personal Data Protection Act, 2023, and
        follow the EU General Data Protection Regulation (GDPR) for the rights and safeguards it gives
        individuals.
      </p>
    </Section>

    <Section title="Cookies" testid="privacy-cookies">
      <p>
        Driveline uses strictly necessary cookies only — to keep you signed in and to protect forms
        against cross-site request forgery. We do not use advertising or cross-site tracking cookies.
        Blocking these cookies will prevent sign-in from working.
      </p>
    </Section>

    <Section title="Your rights" testid="privacy-rights">
      <p>
        You can ask us to give you a copy of your data, correct it, delete it, or stop a particular
        kind of processing. Most of this you can do yourself inside the app; for anything else, email{' '}
        <a className="text-teal-700 underline" href={`mailto:${MERCHANT.email}`}>{MERCHANT.email}</a>{' '}
        and we will respond within 30 days.
      </p>
      <p>
        If you are a vehicle owner and want your details corrected or removed, please contact the
        workshop that holds your record — it is their account and they control it. If you cannot reach
        them, write to us and we will pass the request on.
      </p>
      <p>
        Service and billing emails are part of the subscription and cannot be unsubscribed from while
        an account is active. Any promotional email we send will always carry an unsubscribe link.
      </p>
    </Section>

    <Section title="Children" testid="privacy-children">
      <p>
        Driveline is a business tool and is not directed at anyone under 18. We do not knowingly
        collect data from children.
      </p>
    </Section>

    <Section title="Changes" testid="privacy-changes">
      <p>
        We will update this policy as the product changes. The revision date at the top always
        reflects the current version, and material changes are emailed to account holders before they
        take effect.
      </p>
    </Section>
  </PolicyPage>
);

export default DrivelinePrivacy;
